import { useCallback, useContext, useState } from 'react';
import styled from '@emotion/styled';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import type { Chat } from '@trinity/chat';
import { ChatActionType } from '@trinity/chat';
import { SubmitActionContext } from '@trinity/chat-components/components/Chat/context';
import { CancelAction } from '../presentation/Message.QueryHint/CancelAction';
import { AnalysisQueryHint } from '../presentation/Message.QueryHint/QueryHint';

type Props = {
  response: Chat.QueryResponsePlannerReview;
  // True once a later message exists: the plan can no longer be confirmed.
  disabled: boolean;
};

/**
 * The analytic planner's plan, shown before anything runs. Confirming runs
 * exactly this plan: the reply carries the plan version the server sent,
 * and the server refuses it if the plan changed.
 */
export const PlannerReview = ({ response, disabled }: Props) => {
  const submitAction = useContext(SubmitActionContext);
  const [submitted, setSubmitted] = useState(false);
  const [rememberAnswers, setRememberAnswers] = useState(false);
  const { checkpointId, planVersion, restatement, keyAssumptions, definitions } = response.data;
  const locked = disabled || submitted;

  const reply = useCallback(
    (action: 'confirm' | 'cancel') => {
      if (locked) return;
      setSubmitted(true);
      submitAction?.(
        {
          type: ChatActionType.PlannerReviewReply,
          payload: { checkpointId, planVersion, action, remember: rememberAnswers ? 'answers' : 'none' },
        },
        action === 'confirm' ? 'Run this' : 'Cancel'
      );
    },
    [locked, submitAction, checkpointId, planVersion, rememberAnswers]
  );

  return (
    <Root>
      <Heading>Here's what I'll look up:</Heading>
      <Restatement>{restatement}</Restatement>
      {keyAssumptions.length > 0 && (
        <Section>
          <SectionTitle>Assumptions</SectionTitle>
          <List>
            {keyAssumptions.map((assumption) => (
              <li key={assumption}>{assumption}</li>
            ))}
          </List>
        </Section>
      )}
      {definitions.length > 0 && (
        <Section>
          <SectionTitle>Definitions used</SectionTitle>
          <List>
            {definitions.map((definition) => (
              <li key={definition.term}>
                <strong>{definition.term}</strong>: {definition.summary}{' '}
                <Source>({definition.source})</Source>
              </li>
            ))}
          </List>
        </Section>
      )}
      <FormControlLabel
        control={
          <Checkbox
            checked={rememberAnswers}
            disabled={locked}
            onChange={(_, checked) => setRememberAnswers(checked)}
          />
        }
        label="Remember my answers for this chat (24 hours)"
      />
      <Actions>
        <AnalysisQueryHint value="Run this" disabled={locked} onClick={() => reply('confirm')} />
        <CancelAction displayValue="Cancel" disabled={locked} onClick={() => reply('cancel')} />
      </Actions>
    </Root>
  );
};

const Root = styled('div')({
  boxSizing: 'border-box',
});

const Heading = styled('div')(({ theme }) => ({
  fontSize: 15,
  fontFamily: theme.fonts.semiBold,
  marginBottom: 6,
}));

const Restatement = styled('div')(({ theme }) => ({
  paddingBottom: 10,
  marginBottom: 10,
  borderBottom: `1px solid ${theme.palette.gray.light1}`,
}));

const Section = styled('div')({
  marginBottom: 10,
});

const SectionTitle = styled('div')(({ theme }) => ({
  fontFamily: theme.fonts.semiBold,
  marginBottom: 4,
}));

const List = styled('ul')({
  margin: 0,
  paddingLeft: 20,
});

const Source = styled('span')(({ theme }) => ({
  color: theme.palette.gray.dark1,
}));

const Actions = styled('div')({
  marginTop: 12,

  '> :not(:last-child)': {
    marginBottom: 10,
  },
});
